import type { RetailerOrder, SharedOrder, WarehouseSummaryType } from "@shared/types/Order";

interface OrderStatusSummary {
  [key: string]: {
    count: number;
    totalLocations: number;
  };
}

import {
  OHLQ_DISTRIBUTOR_ID,
  VT_802_SPIRITS_DISTRIBUTOR_ID,
} from "@shared/services/retailer_user_authentication/constants";

const DEFAULT_DISPLAY_STATE = "";
const OHLQ_PICKED_DISPLAY_STATE = "Picked";
const VERMONT_READY_FOR_PICKUP_DISPLAY_STATE = "Ready for Pickup";
const VERMONT_COMPLETE_DISPLAY_STATE = "Complete";
const VERMONT_REJECTED_DISPLAY_STATE = "Rejected";
const VERMONT_SENT_DISPLAY_STATE = "Sent";
const ORDER_ACCEPTED_STATE = "accepted";
const ORDER_PENDING_STATE = "pending";
const ORDER_REJECTED_STATE = "rejected";

export const statusBarDisplayState = (order: RetailerOrder | SharedOrder) =>
  order.distributor_id === VT_802_SPIRITS_DISTRIBUTOR_ID
    ? order.warehouse_summaries[0].display_state
    : order.display_state;

export const orderStatusSummary = (order: RetailerOrder): OrderStatusSummary => {
  // Only process warehouse summaries for Vermont distributor
  if (order.distributor_id !== VT_802_SPIRITS_DISTRIBUTOR_ID) {
    return {} as OrderStatusSummary;
  }

  const totalLocations = order.warehouse_summaries.length;

  // Count occurrences of each display state
  const statusCounts = order.warehouse_summaries.reduce(
    (acc, summary) => {
      const displayState = summary.display_state;
      acc[displayState] = (acc[displayState] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  // Transform counts into the expected format
  return Object.entries(statusCounts).reduce((acc, [displayState, count]) => {
    acc[displayState] = {
      count,
      totalLocations: totalLocations,
    };
    return acc;
  }, {} as OrderStatusSummary);
};

export const statusGreen = (order: RetailerOrder | SharedOrder, displayState: string) => {
  return order.distributor_id === VT_802_SPIRITS_DISTRIBUTOR_ID
    ? vermontStatusGreen(order.distributor_id, displayState)
    : order.state == ORDER_ACCEPTED_STATE ||
        (order.state == ORDER_PENDING_STATE &&
          !!order.seen_by_rep_at &&
          !!order.distributor_brandless);
};

const vermontStatusGreen = (distributorId: number, displayState: string) => {
  return (
    distributorId === VT_802_SPIRITS_DISTRIBUTOR_ID &&
    (displayState === VERMONT_READY_FOR_PICKUP_DISPLAY_STATE ||
      displayState === VERMONT_COMPLETE_DISPLAY_STATE)
  );
};

export const statusGray = (order: RetailerOrder | SharedOrder, displayState: string) => {
  return order.distributor_id === VT_802_SPIRITS_DISTRIBUTOR_ID
    ? vermontStatusGray(order.distributor_id, displayState)
    : order.state == ORDER_PENDING_STATE && (!order.distributor_brandless || !order.seen_by_rep_at);
};

const vermontStatusGray = (distributorId: number, displayState: string) => {
  return (
    distributorId === VT_802_SPIRITS_DISTRIBUTOR_ID && displayState === VERMONT_SENT_DISPLAY_STATE
  );
};

export const statusRed = (displayState: string) => {
  return displayState?.toLowerCase() === ORDER_REJECTED_STATE;
};

export const isUpdatedOrder = (
  order: RetailerOrder | SharedOrder,
  locationDisplayState: string = DEFAULT_DISPLAY_STATE,
) => {
  if (order.distributor_id === OHLQ_DISTRIBUTOR_ID) {
    return (
      order.display_state === OHLQ_PICKED_DISPLAY_STATE ||
      order.state === ORDER_ACCEPTED_STATE ||
      order.state === ORDER_REJECTED_STATE
    );
  } else if (order.distributor_id === VT_802_SPIRITS_DISTRIBUTOR_ID) {
    return (
      locationDisplayState === VERMONT_READY_FOR_PICKUP_DISPLAY_STATE ||
      locationDisplayState === VERMONT_COMPLETE_DISPLAY_STATE ||
      locationDisplayState === VERMONT_REJECTED_DISPLAY_STATE
    );
  } else {
    return order.state === ORDER_ACCEPTED_STATE;
  }
};

export const vermontOrderReadyForPickup = (orderDetail: WarehouseSummaryType) =>
  orderDetail.distributor_id === VT_802_SPIRITS_DISTRIBUTOR_ID &&
  orderDetail.display_state === VERMONT_READY_FOR_PICKUP_DISPLAY_STATE;
